import React, { useEffect, useState } from "react";
import { FaLocationDot } from "react-icons/fa6";
import { TbMailFilled } from "react-icons/tb";
import { Link, useNavigate } from "react-router-dom";
import Smartphone from "../../../helper/svg/smartphone.svg";
import PRofileID from "../../../helper/svg/ID.svg";
import { IoIosArrowRoundBack } from "react-icons/io";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getUserById } from "../../../api/common/index";
import { updateSettings, updatePassword, deleteUser, getReferralStats } from "../../../api/common/settings";
import toast from "react-hot-toast";
import { useTranslation } from "../../../hooks/useTranslation";
import { useLanguage } from "../../../context/LanguageContext";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Swal from "sweetalert2";
import PupupImage from "../../../helper/images/PopupImage.png";
import Invitation from "../../../helper/images/invitation 1.png";
import { LuCopy } from "react-icons/lu";
import OfferIcon from "../../../helper/images/offer.png";
import UsersIcon from "../../../helper/svg/users.svg";
import { FiCamera } from "react-icons/fi";

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "U";
  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
};

const AdminSettings = () => {
  const { t } = useTranslation();
  const { setLanguage } = useLanguage();
  const passwordValidation = t("passwordValidation");
  const [enabled, setEnabled] = useState(true);
  const queryClient = useQueryClient();
  const [previewImage, setPreviewImage] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    address: "",
    phoneNumber: "",
    language: "",
    timeFormat: "",
    timeZone: "",
    firstDayOfWeek: "",
    profilePicture: null,
    notifications: true,
    oldPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [isEditing, setIsEditing] = useState(false);
  const [hasShownEditHint, setHasShownEditHint] = useState(false);
  const navigate = useNavigate();
  const [showPasswords, setShowPasswords] = useState({
    oldPassword: false,
    newPassword: false,
    confirmPassword: false,
  });


  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["user"],
    queryFn: getUserById,
    keepPreviousData: true,
  });

  const { data: referralStats } = useQuery({
    queryKey: ["referralStats"],
    queryFn: getReferralStats,
    keepPreviousData: true,
  });

  const user = data?.data;
  const promoCode = user?.promoCode || referralStats?.data?.promoCode || "";
  const referralCount = referralStats?.data?.referralCount || 0;
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const updateMutation = useMutation({
    mutationFn: (payload) => updateSettings(payload),
    onSuccess: () => {
      const successMessage = t("userprofile").toastMessages?.updateSuccess || "Profile details updated successfully";
      toast.success(successMessage);
      if (formData.language) {
        setLanguage(formData.language);
      }
      queryClient.invalidateQueries(["user"]);
      queryClient.invalidateQueries(["userLanguage"]);
    },
    onError: () => toast.error("Failed to update settings"),
  });


  const updatePasswordMutation = useMutation({
    mutationFn: (payload) => updatePassword(payload),
    onSuccess: () => {
      queryClient.invalidateQueries(["user"]);

      toast.success("Password updated successfully!");
      setFormData((prev) => ({
        ...prev,
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      }));
    },
    onError: (error) => {
      const backendMessage = error?.response?.data?.message || "";
      let errorMessage = "Something went wrong";
      if (backendMessage && (
          backendMessage.toLowerCase().includes("passwords do not match") ||
          backendMessage.toLowerCase().includes("password mismatch") ||
          backendMessage === "New passwords do not match."
        )) {
        errorMessage = passwordValidation?.mismatch || "New passwords do not match.";
      } else if (backendMessage) {
        errorMessage = backendMessage;
      }
      toast.error(errorMessage);
    },
  });


  const handlePasswordChange = async () => {
    const { oldPassword, newPassword, confirmPassword } = formData;

    if (!oldPassword || !newPassword || !confirmPassword) {
      toast.error(
        passwordValidation?.allFieldsRequired || "All password fields are required."
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(
        passwordValidation?.mismatch || "New passwords do not match."
      );
      return;
    }

    try {
      updatePasswordMutation.mutate({
        oldPassword,
        newPassword,
        confirmPassword,
      });
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error updating password.");
    }
  };

  const { mutate: deleteUserMutation } = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      navigate("/login")
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("addnewhotel").toastMessages.deleteError;
      toast.error(errorMessage);
    },
  });

  const handleDelete = () => {
    Swal.fire({
      title: t("userHotelDetail").deleteConfirm.title,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("userHotelDetail").deleteConfirm.confirm,
      cancelButtonText: t("userHotelDetail").deleteConfirm.cancel,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteUserMutation();
      }
    });
  };

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        address: user.address || "",
        phoneNumber: user.phoneNumber || "",
        language: user.language || "",
        timeFormat: user.timeFormat || "",
        timeZone: user.timeZone || "",
        firstDayOfWeek: user.firstDayOfWeek || "",
        profilePicture: user.profilePicture || "",
        notifications: user.notifications ?? true,
      });
      setEnabled(user.notifications ?? true);
    }
  }, [user]);

  const activateEditing = () => {
    if (isEditing) return;
    setIsEditing(true);
    if (!hasShownEditHint) {
      toast(
        t("userprofile").editHint ||
          "Edit mode enabled. Save changes from the left panel."
      );
      setHasShownEditHint(true);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "file") {
      const file = files[0];
      setFormData((prev) => ({
        ...prev,
        profilePicture: file,
      }));
      setPreviewImage(URL.createObjectURL(file));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleUpdate = () => {
    const data = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "profilePicture" && value instanceof File) {
        data.append(key, value);
      } else {
        data.append(key, value);
      }
    });
    updateMutation.mutate(data);
    setIsEditing(false);
  };

  return (
    <>
      <div className="flex gap-[10px] items-center">
        <div className="w-[32px] h-[32px] bg-[#4CAF50] rounded-[50%] flex items-center justify-center hover:bg-[#FF9D00]">
          <IoIosArrowRoundBack
            size={24}
            onClick={() => navigate(-1)}
            className="text-[#fff]"
          />
        </div>
        <h2 className="text-[22px] font-medium">{t("userprofile").settings}</h2>
      </div>
      <div className="grid xl:grid-cols-[auto_1fr] grid-cols-1 gap-7 md:mb-[50px] mb-[30px] md:mt-[39px] mt-5">
        {/* ===========user details==================== */}
        <div className="w-full xl:max-w-[360px] bg-white p-2 box-shadow-detail">
          <div className="bg-[#FFFAF2] h-[132px] rounded-[3.56px]"></div>

          <label
            htmlFor="profilePictureInput"
            className={`cursor-pointer w-full flex justify-center ${
              isEditing ? "opacity-100" : "opacity-95"
            }`}
            onClick={(e) => {
              if (!isEditing) {
                e.preventDefault();
                activateEditing();
              }
            }}
          >
            <div className="relative w-full max-w-[159px] h-[159px] mx-auto -mt-[80px]">
              {previewImage || user?.profilePicture ? (
                <img
                  src={previewImage || user?.profilePicture}
                  alt="Profile"
                  className="w-full h-full rounded-full border-4 border-white object-cover"
                />
              ) : (
                <div className="w-full h-full rounded-full border-4 border-white bg-gradient-to-r from-[#4CAF50] to-[#1B7200] flex items-center justify-center text-white text-4xl font-semibold">
                  {getInitials(user?.name)}
                </div>
              )}
              {isEditing && (
                <span className="absolute bottom-2 right-2 bg-white rounded-full shadow-md p-2">
                  <FiCamera className="text-[#4CAF50]" size={16} />
                </span>
              )}
            </div>
            <input
              id="profilePictureInput"
              type="file"
              name="profilePicture"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleChange}
              disabled={!isEditing}
            />
          </label>
          <h2 className="text-xl font-medium text-[#353535] text-center mt-5">
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="border border-[#ccc] rounded px-2 py-1 w-full text-center"
              />
            ) : (
              user?.name
            )}
          </h2>
          <div className="bg-[#E1EDDE] px-2 py-1 text-center text-[#1B7200] rounded-[7.11px] w-max mx-auto mt-[7px] mb-5">
            {user?.role}
          </div>
          <div className="py-5 px-7 border-t border-[#E0E2E7]">
            <ul>
              <li className="grid grid-cols-[auto_1fr] gap-[14px] mb-4 justify-start">
                <span className="bg-[#F0F1F3] rounded-[50%] w-[35.56px] h-[35.56px] flex items-center justify-center">
                  <img src={PRofileID} alt="" className="" />
                </span>
                <div>
                  <h5 className="text-[#667085] text-base mb-2">
                    {t("userprofile").userId}
                  </h5>
                  <p className="text-[#353535] text-base">{user?._id}</p>
                </div>
              </li>
              <li className="grid grid-cols-[auto_1fr] gap-[14px] mb-4 justify-start">
                <span className="bg-[#F0F1F3] rounded-[50%] w-[35.56px] h-[35.56px] flex items-center justify-center">
                  <TbMailFilled className="text-[#667085] text-base" />
                </span>
                <div>
                  <h5 className="text-[#667085] text-base mb-2">
                    {t("userprofile").email}
                  </h5>
                  <p className="text-[#353535] text-base">{user?.email}</p>
                </div>
              </li>
              <li className="grid grid-cols-[auto_1fr] gap-[14px] mb-4 justify-start">
                <span className="bg-[#F0F1F3] rounded-[50%] w-[35.56px] h-[35.56px] flex items-center justify-center">
                  <FaLocationDot className="text-[#667085] text-base" />
                </span>
                <div>
                  <h5 className="text-[#667085] text-base mb-2">
                    {t("userprofile").address}
                  </h5>
                  <p className="text-[#353535] text-base">
                    {isEditing ? (
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        className="border border-[#ccc] rounded px-2 py-1 w-full"
                      />
                    ) : (
                      user?.address || "N/A"
                    )}
                  </p>
                </div>
              </li>
              <li className="grid grid-cols-[auto_1fr] gap-[14px] mb-4 justify-start">
                <span className="bg-[#F0F1F3] rounded-[50%] w-[35.56px] h-[35.56px] flex items-center justify-center">
                  <img src={Smartphone} alt="" className="" />
                </span>
                <div>
                  <h5 className="text-[#667085] text-base mb-2">
                    {t("userprofile").phoneNumber}
                  </h5>
                  <p className="text-[#353535] text-base ">
                    {isEditing ? (
                      <input
                        type="text"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        onChange={handleChange}
                        className="border border-[#ccc] rounded px-2 py-1 w-full"
                      />
                    ) : (
                      user?.phoneNumber
                    )}
                  </p>
                </div>
              </li>
              {/* Promo Code Section */}
              <li className="grid grid-cols-[auto_1fr] gap-[14px] mb-4 justify-start">
                <span className="bg-[#F0F1F3] rounded-[50%] w-[35.56px] h-[35.56px] flex items-center justify-center">
                  <TbMailFilled className="text-[#667085] text-base" />
                </span>
                <div>
                  <h5 className="text-[#667085] text-base mb-2">
                    {t("userprofile").promoCode || "Promo Code"}
                  </h5>
                  <p className="text-[#353535] text-base font-semibold">
                    {promoCode || "N/A"}
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <div className="px-[16px] mt-4 flex flex-col gap-3">
            {!isEditing ? (
              <button
                onClick={activateEditing}
                className="w-full text-center bg-[#EAF7E9] text-[#1B7200] font-semibold py-2 rounded-[10px] border border-[#B7E2B5] shadow-sm hover:bg-[#DFF1DE] transition-colors"
              >
                {t("userprofile").editProfile}
              </button>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3 mb-[24px]">
                <button
                  onClick={handleUpdate}
                  className="flex-1 min-w-[120px] bg-[#4CAF50] text-white font-semibold py-2 rounded-[8px] shadow-sm hover:bg-[#43A047] transition-colors"
                >
                  {t("userprofile").save}
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="flex-1 min-w-[120px] border border-[#FF9D9D] text-[#FF0000] font-semibold py-2 rounded-[8px] hover:bg-[#FFF2F2] transition-colors"
                >
                  {t("userprofile").cancel}
                </button>
              </div>
            )}

            <button
              onClick={() => setIsPopupOpen(true)}
              className="bg-[#FF9D00] text-white text-[17px] font-semibold text-left items-start py-[8px] px-[12px] rounded-[10px] flex gap-[10px] w-full"
            >
              <img src={UsersIcon} alt="" />
              {t("userprofile").inviteFriends}
            </button>
          </div>
        </div>
        {/* ============user settings section=============== */}
        <div className="box-shadow-detail md:p-[25px] flex flex-col justify-between md:pb-[47px] py-[20px] px-[15px] bg-white">
          <div>
            {!isEditing && (
              <div className="flex items-center justify-between bg-[#F6FFF4] border border-[#CBEFD1] rounded-[10px] px-4 py-3 mb-5 text-sm text-[#1B7200]">
                <span>
                  {t("userprofile").lockedMessage ||
                    "Preferences are currently locked."}
                </span>
                <button
                  onClick={activateEditing}
                  className="text-[#4CAF50] font-semibold"
                >
                  {t("userprofile").editProfile}
                </button>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-[300px_auto] md:gap-7 gap-3  mb-[25px]">
              <div className="w-full max-w-[300px] inline-block">
                <h3 className="text-[#18181B] text-lg font-medium mb-[5px]">
                  {t("userprofile").timeZone}
                </h3>
                <p className="text-[15px] text-[#18181B]">
                  {t("userprofile").selectTimeZone}
                </p>
              </div>
              <select
                className={`border border-[#E0E0E0] px-[13px] py-[12px] text-base focus:outline-none rounded-[5px] w-full max-w-[314px] ${
                  !isEditing ? "cursor-pointer" : ""
                }`}
                name="timeZone"
                value={formData.timeZone}
                onChange={handleChange}
                onFocus={activateEditing}
              >
                <option value="">
                  {t("userprofile").selectTimeZone}
                </option>
                <option value="UTC">UTC</option>
                <option value="Asia/Calcutta">Asia/Calcutta</option>
                <option value="Asia/Kolkata">Asia/Kolkata</option>
                <option value="America/New_York">America/New_York</option>
                <option value="Europe/London">Europe/London</option>
                <option value="Europe/Paris">Europe/Paris</option>
                <option value="Asia/Tokyo">Asia/Tokyo</option>
                <option value="Australia/Sydney">Australia/Sydney</option>
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[300px_auto] md:gap-7 gap-3  mb-[25px]">
              <div className="w-full max-w-[300px] inline-block">
                <h3 className="text-[#18181B] text-lg font-medium mb-[5px]">
                  {t("userprofile").timeFormat}
                </h3>
                <p className="text-[15px] text-[#18181B]">
                  {t("userprofile").selectTimeFormat}
                </p>
              </div>
              <select
                className={`border border-[#E0E0E0] px-[13px] py-[12px] text-base focus:outline-none rounded-[5px] w-full max-w-[314px] ${
                  !isEditing ? "cursor-pointer" : ""
                }`}
                name="timeFormat"
                value={formData.timeFormat}
                onChange={handleChange}
                onFocus={activateEditing}
              >
                <option value="">
                  {t("userprofile").selectTimeFormat}
                </option>
                <option value="24">24-hr format (military clock)</option>
                <option value="12"> 12-hr format (AM/PM)</option>
              </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[300px_auto] md:gap-7 gap-3  mb-[25px]">
              <div className="w-full max-w-[300px] inline-block">
                <h3 className="text-[#18181B] text-lg font-medium mb-[5px]">
                  {t("userprofile").firstDayOfWeek}
                </h3>
                <p className="text-[15px] text-[#18181B]">
                  {t("userprofile").whenWeekStart}
                </p>
              </div>
              <select
                className={`border border-[#E0E0E0] px-[13px] py-[12px] text-base focus:outline-none rounded-[5px] w-full max-w-[314px] ${
                  !isEditing ? "cursor-pointer" : ""
                }`}
                name="firstDayOfWeek"
                value={formData.firstDayOfWeek}
                onChange={handleChange}
                onFocus={activateEditing}
              >
                <option value="">
                  {t("userprofile").Weekday}
                </option>

                <option value="monday">
                  {t("userprofile").options.monday}
                </option>
                <option value="tuesday">
                  {t("userprofile").options.tuesday}
                </option>
                <option value="wednesday">
                  {t("userprofile").options.wednesday}
                </option>
                <option value="thursday">
                  {t("userprofile").options.thursday}
                </option>
                <option value="friday">
                  {t("userprofile").options.friday}
                </option>
                <option value="saturday">
                  {t("userprofile").options.saturday}
                </option>
                <option value="sunday">
                  {t("userprofile").options.sunday}
                </option>
              </select>
            </div>
            <hr className="border-t border-[#E0E0E0] w-full mb-[25px]" />
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] md:gap-7 gap-3   md:gap-[55px] items-center max-w-[668px] mb-[25px]">
              <div className="w-full max-w-[553px] inline-block">
                <h3 className="text-[#18181B] text-lg font-medium mb-[5px]">
                  {t("userprofile").notification}
                </h3>
                <p className="text-[15px] text-[#18181B]">
                  {t("userprofile").notificationDesc}
                </p>
              </div>

              <div
                onClick={() => {
                  if (!isEditing) {
                    activateEditing();
                  }
                  setEnabled((prev) => !prev);
                  setFormData((prev) => ({
                    ...prev,
                    notifications: !prev.notifications,
                  }));
                }}
                className={`w-[60px] h-[31px] flex items-center rounded-full p-1 cursor-pointer transition-colors ${formData.notifications ? "bg-[#4CAF50]" : "bg-[#e1e1e1]"
                  }`}
              >
                <div
                  className={`bg-white w-[25px] h-[25px] rounded-full shadow-md transform transition-transform ${formData.notifications ? "translate-x-7" : "translate-x-0"
                    }`}
                />
              </div>
            </div>
            <hr className="border-t border-[#E0E0E0] w-full mb-[25px]" />
            <div className="grid grid-cols-1 md:grid-cols-[300px_auto] md:gap-7 gap-3  mb-[25px]">
              <div className="w-full max-w-[300px] inline-block">
                <h3 className="text-[#18181B] text-lg font-medium mb-[5px]">
                  {t("userprofile").language}
                </h3>
                <p className="text-[15px] text-[#18181B]">
                  {t("userprofile").selectLanguage}
                </p>
              </div>
              <select
                className={`border border-[#E0E0E0] px-[13px] py-[12px] text-base focus:outline-none rounded-[5px] w-full max-w-[314px] ${
                  !isEditing ? "cursor-pointer" : ""
                }`}
                name="language"
                value={formData.language}
                onChange={handleChange}
                onFocus={activateEditing}
              >
                <option value="">
                  {t("userprofile").selectLanguage}
                </option>
                <option value="English">English</option>
                <option value="Russian">Russian</option>
                <option value="Georgian">Georgian</option>
              </select>
            </div>



            <hr className="border-t border-[#E0E0E0] w-full mb-[25px]" />
            <div className="grid grid-cols-1 md:grid-cols-[300px_auto] md:gap-7 gap-3 mb-[25px]">
              <div className="w-full max-w-[300px] inline-block">
                <h3 className="text-[#18181B] text-lg font-medium mb-[5px]">
                  {t("userprofile").changePassword || "Change Password"}
                </h3>
                <p className="text-[15px] text-[#18181B]">
                  {t("userprofile").updatePassword || "Update your account password"}
                </p>
              </div>

              <div className="flex flex-col gap-3 max-w-[400px]">
                {[
                  {
                    name: "oldPassword",
                    placeholder: t("userprofile")?.enterOldPassword || "Enter Old Password",
                  },
                  {
                    name: "newPassword",
                    placeholder: t("userprofile")?.enterNewPassword || "Enter New Password",
                  },
                  {
                    name: "confirmPassword",
                    placeholder: t("userprofile")?.confirmNewPassword || "Confirm New Password",
                  },
                ].map((field) => (
                  <div key={field.name} className="relative">
                    <input
                      type={showPasswords[field.name] ? "text" : "password"}
                      name={field.name}
                      placeholder={field.placeholder}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                      className="w-full border border-[#E0E0E0] px-[13px] py-[12px] text-base focus:outline-none rounded-[5px]"
                    />

                    {/* 👁 Eye Icon (Font Awesome) */}
                    <span
                      onClick={() =>
                        setShowPasswords((prev) => ({
                          ...prev,
                          [field.name]: !prev[field.name],
                        }))
                      }
                      className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer text-[#7E7E7E]"
                    >
                      {showPasswords[field.name] ? (
                        <FaEyeSlash size={18} />
                      ) : (
                        <FaEye size={18} />
                      )}
                    </span>
                  </div>
                ))}

                <button
                  onClick={handlePasswordChange}
                  className="bg-[#4CAF50] text-white py-[10px] rounded-[5px] mt-2 hover:bg-[#43A047] transition-colors"
                >
                  {t("userprofile").updatePasswordButton || "Update Password"}
                </button>
              </div>
            </div>
          </div>


          {/* <div>
            <hr className="border-t border-[#E0E0E0] w-full mb-[25px]" />
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] md:gap-7 gap-3 justify-between  xl:gap-[55px] items-center  mb-[25px]">
              <div className="w-full max-w-[521px] inline-block">
                <h3 className="text-[#18181B] text-lg font-medium mb-[5px]">
                  {t("userprofile").deleteAccount}
                </h3>
                <p className="text-[15px] text-[#18181B]">
                  {t("userprofile").deleteAccountDesc}
                </p>
              </div>

              <button
                onClick={handleDelete}
                className="text-[#FF0000] text-[17px] inline-block font-medium"
              >
                {t("userprofile").deleteAccount}
              </button>
            </div>
          </div> */}
        </div>
      </div>

      {/* Modal Popup */}
      <PopupModal isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
        <div className="w-full max-w-[579px] mx-auto text-center rounded-[20px]">
          <div className="bg-[#FFF3EF] grid grid-cols-1 sm:grid-cols-[287px_auto] gap-7 rounded-t-[20px] px-[27px] items-end">
            <div className="text-left py-[25px]">
              <h2 className="text-xl font-semibold leading-[30px] mb-3">
                {t("userprofile").referFriend}
              </h2>
              <Link
                to="#"
                className="w-full rounded-[2px] bg-[#4CAF50] text-[15px] text-white text-center font-bold py-[6px] px-[14px] green-shadow hover:bg-[#209b25] inline-block"
              >
                {t("userprofile").inviteFriends}
              </Link>
            </div>
            <img src={PupupImage} alt="" className="w-full" />
          </div>
          <div className="py-[23px] px-[26px] text-left">
            <h2 className="text-lg font-medium leading-[30px] mb-[10px]">
              {t("userprofile").howToDiscount}
            </h2>
            <div className="flex gap-[15px] justify-between mb-[13px] items-start">
              <div className="flex gap-[15px] items-start">
                <img src={Invitation} alt="" />
                <p className="text-sm max-w-[389px] text-black">
                  {t("userprofile").inviteStep}
                  <span className="text-[#4CAF50] font-semibold leading-[20px] ml-1">
                    {promoCode}
                  </span>
                </p>
              </div>
              <button
                onClick={() => {
                  if (promoCode) {
                    navigator.clipboard.writeText(promoCode);
                    toast.success(t("userprofile").copied || "Promo code copied!");
                  }
                }}
                className="cursor-pointer"
              >
                <LuCopy size={16} className="text-[#898989] hover:text-[#4CAF50]" />
              </button>
            </div>
            <div className="flex gap-[15px] justify-between text-black items-start">
              <div className="flex gap-[15px] items-start">
                <img src={OfferIcon} alt="" />
                <p className="text-sm max-w-[389px]">
                  {t("userprofile").getReferralAmount || `Get referral amount when users sign up with your promo code! You have referred ${referralCount} user${referralCount !== 1 ? 's' : ''} so far.`}
                </p>
              </div>
            </div>
          </div>
        </div>
      </PopupModal>
    </>
  );
};

const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] sm:max-w-[90%] sm:w-[579px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

export default AdminSettings;
